<template>
  <div
    class="c_product_header_standard__wrapper"
    :data-theme="data.theme ?? 'dark'"
    :class="[
      data.alignment === 'left' ? 'c_product_header_standard--left' : 'c_product_header_standard--center',
      { 'c_product_header_standard--no-bottom-padding': data.mediaSize === 'expanded' },
    ]"
  >
    <div class="c_product_header_standard c_wrapper">
      <div class="c_product_header__content_wrapper">
        <h3 v-if="data.sectionTitle" v-html="data.sectionTitle"></h3>
        <h4 v-if="data.subtitle" v-html="data.subtitle"></h4>
        <div class="c_2022--body" v-if="data.description" v-html="data.description"></div>
      </div>
      <div class="c_product_header_standard__links_wrapper" v-if="ctas || links">
        <ul v-if="ctas" class="c_product_header_standard__ctas">
          <li v-for="(linkData, index) in ctas" :key="`c_product_header_standard_cta--${index}`">
            <!-- Leaving Site -->
            <Button
              v-if="linkData.link?.url && linkData?.enableModal"
              :class="[
                linkData.linkStyle === 'ctaDefault'
                  ? 'c_button_2022 c_button_2022--primary'
                  : 'c_button_2022 c_button_2022__secondary',
              ]"
              :aria-label="linkData?.link?.ariaLabel"
              @onClick="triggerExternalLinkModal(linkData)"
              unstyle
              reset
            >
              {{ linkData.link?.customText || linkData.link?.text || $t('Learn More') }}
            </Button>

            <!-- Video (YouTube / MP4) -->
            <Button
              v-else-if="hasVideo(linkData.link?.element) && linkData.link?.url"
              :class="[
                linkData.linkStyle === 'ctaDefault'
                  ? 'c_button_2022 c_button_2022--primary'
                  : 'c_button_2022 c_button_2022__secondary',
              ]"
              :aria-label="linkData?.link?.ariaLabel"
              @onClick="handleVideoClick(linkData)"
              unstyle
              reset
            >
              {{ linkData.link?.customText || linkData.link?.text || $t('Learn More') }}
            </Button>

            <!-- Normal link -->
            <a
              v-else-if="linkData.link?.url"
              :href="linkData.link?.url"
              :target="linkData.link?.target"
              :aria-label="linkData.link?.ariaLabel"
              :class="[
                linkData.linkStyle === 'ctaDefault'
                  ? 'c_button_2022 c_button_2022--primary'
                  : 'c_button_2022 c_button_2022__secondary',
              ]"
            >
              {{ linkData.link?.customText || linkData.link?.text || $t('Learn More') }}
            </a>
          </li>
        </ul>
        <ul v-if="links" class="c_product_header_standard__links">
          <li v-for="(linkData, index) in links" :key="`c_product_header_standard_link--${index}`">
            <!-- Leaving Site -->
            <Button
              v-if="linkData.link?.url && linkData.enableModal"
              class="c_product_header_standard__linkDefault"
              :aria-label="linkData.link?.ariaLabel"
              @onClick="triggerExternalLinkModal(linkData)"
              unstyle
              reset
            >
              <span>{{ linkData.link?.customText || linkData.link?.text || $t('Learn More') }}</span>
              <IconSVG width="14" height="14" handle="new_window" v-if="linkData.link?.target" />
            </Button>

            <!-- Video Link (YouTube / MP4) -->
            <Button
              v-else-if="hasVideo(linkData.link?.element) && linkData.link?.url && !linkData.enableModal"
              class="c_product_header_standard__linkDefault"
              :aria-label="linkData.link?.ariaLabel"
              @onClick="handleVideoClick(linkData)"
              unstyle
              reset
            >
              <span>{{ linkData.link?.customText || linkData.link?.text || $t('Learn More') }}</span>
              <IconSVG width="14" height="14" handle="new_window" v-if="linkData.link?.target" />
            </Button>

            <!-- Regular Link -->
            <a
              v-else-if="linkData.link?.url && !linkData.enableModal"
              :href="linkData.link?.url"
              :target="linkData.link?.target"
              :aria-label="linkData.link?.ariaLabel"
              class="c_product_header_standard__linkDefault"
            >
              <span>{{ linkData.link?.customText || linkData.link?.text || $t('Learn More') }}</span>
              <IconSVG width="14" height="14" handle="new_window" v-if="linkData.link?.target" />
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div
      class="c_product_header_standard__media"
      v-if="data.media"
      :class="[
        data.mediaSize === 'condensed' ? 'c_product_header_standard--condensed' : 'c_product_header_standard--expanded',
      ]"
    >
      <div
        :style="data.maxHeight ? `maxHeight: ${data.maxHeight};` : ''"
        class="c_product_header_standard__media_wrapper"
      >
        <MediaImage
          v-if="data.media?.[0]?.uncropped1920w"
          :sources="[data.media[0].uncropped1920w]"
          :alt="data.media[0].title"
        />
      </div>
    </div>
    <Overlay
      class="c_external_link__overlay"
      ref="external_link_overlay"
      :show-close-button="true"
      rounded
      @overlayHide="clearExternalLinkModal"
    >
      <ExternalLinkModal
        :disclaimers="externalLinkDisclaimer"
        :url="externalLinkUrl"
        @hideExternalLinkModal="hideExternalLinkModal"
      />
    </Overlay>
    <Overlay
      class="c_product_header_standard__overlay"
      ref="videoBannerOverlay"
      theme="full"
      :show-close-button="true"
      @close="closeVideoModal"
    >
      <div class="c_box--pos--relative c_product_header_standard__overlay__video">
        <!-- YouTube -->
        <MediaVideo
          v-if="videoSource === 'youtube'"
          loading="eager"
          :source="videoSource"
          :video-id="videoId"
          controls
          autoplay
          playsinline
        />

        <!-- MP4 -->
        <video
          v-else-if="videoSource === 'mp4' && videoUrl"
          class="c_product_header_standard__overlay__video__player"
          :src="videoUrl"
          controls
          playsinline
          autoplay
        />
      </div>
    </Overlay>
  </div>
</template>

<script>
import { defineComponent, ref } from '@nuxtjs/composition-api';
import MediaImage from 'Components/image/MediaImage.vue';
import MediaVideo from 'Components/video/MediaVideo.vue';
import Button from 'Components/button/Button.vue';
import Overlay from 'Components/overlay/Overlay.vue';
import ExternalLinkModal from 'Components/external_link_modal/ExternalLinkModal.vue';
import { getVideoPayload, hasVideo } from 'JS/global.js';

export default defineComponent({
  name: 'ProductHeaderStandard',
  components: {
    Button,
    ExternalLinkModal,
    MediaImage,
    MediaVideo,
    Overlay,
  },
  props: {
    data: { type: Object, default: () => {} },
  },
  setup(props) {
    // Separate links and ctas
    const links = (props.data.links || []).filter((link) => link.linkStyle === 'linkDefault');
    const ctas = (props.data.links || []).filter((link) => link.linkStyle !== 'linkDefault');

    // External Link Modal functionality
    const externalLinkDisclaimer = ref([]);
    const externalLinkUrl = ref('');
    const external_link_overlay = ref(null);
    const triggerExternalLinkModal = (link) => {
      if (link?.disclaimerFootnote?.[0]) {
        externalLinkDisclaimer.value.push(link.disclaimerFootnote[0]);
      }
      externalLinkUrl.value = link.link?.url || 'https://kodak.com/en/';
      if (external_link_overlay.value && external_link_overlay.value.show) {
        external_link_overlay.value.show();
      }
    };

    const clearExternalLinkModal = () => {
      externalLinkDisclaimer.value = [];
      externalLinkUrl.value = '';
    };
    const hideExternalLinkModal = () => {
      if (external_link_overlay?.value && external_link_overlay?.value?.hide) {
        external_link_overlay.value.hide();
      }
    };

    const videoBannerOverlay = ref(null);
    const videoSource = ref('');
    const videoId = ref('');
    const videoUrl = ref('');

    const openVideoModal = (el) => {
      const video = getVideoPayload(el);
      if (!video) return;

      if (video.type === 'youtube') {
        videoSource.value = video.source || 'youtube';
        videoId.value = video.id;
        videoUrl.value = null;
      } else if (video.type === 'mp4') {
        videoSource.value = 'mp4';
        videoId.value = null;
        videoUrl.value = video.url;
      }
      videoBannerOverlay.value?.show?.();
    };
    const closeVideoModal = () => {
      videoBannerOverlay.value?.hide?.();
      videoSource.value = null;
      videoId.value = null;
      videoUrl.value = null;
    };
    const handleVideoClick = (linkData) => {
      const el = linkData?.link?.element;
      openVideoModal(el);
    };

    return {
      clearExternalLinkModal,
      closeVideoModal,
      ctas,
      external_link_overlay,
      externalLinkDisclaimer,
      externalLinkUrl,
      getVideoPayload,
      handleVideoClick,
      hasVideo,
      hideExternalLinkModal,
      links,
      openVideoModal,
      triggerExternalLinkModal,
      videoBannerOverlay,
      videoId,
      videoSource,
      videoUrl,
    };
  },
});
</script>

<style lang="scss">
.c_product_header_standard__wrapper {
  padding: px_to_rem(40) 0 0;
  @include mq($mq_sm_to_md) {
    padding: px_to_rem(80) 0;
  }
  &[data-theme='dark'] {
    --header-standard-bg-color: var(--color-black);
    --header-standard-color: var(--color-white);
    --header-standard-link-color: var(--color-white);
    --header-standard-text-link-color: var(--color-yellow);
  }
  &[data-theme='light'] {
    --header-standard-bg-color: var(--color-white);
    --header-standard-color: var(--color-black);
    --header-standard-link-color: var(--color-black);
    --header-standard-text-link-color: var(--color-red);
  }
  background-color: var(--header-standard-bg-color);
  color: var(--header-standard-color);

  .c_product_header_standard {
    display: flex;
    gap: px_to_rem(40);
    @include mq($mq_md_to_lg) {
      gap: px_to_rem(80);
    }
    h3 {
      @include font('graphik_bold');
      font-size: px_to_rem(40);
      line-height: px_to_rem(48);
      margin-bottom: px_to_rem(16);
    }
    h4 {
      @include font('graphik_semibold');
      font-size: px_to_rem(24);
      line-height: px_to_rem(32);
      margin-bottom: px_to_rem(16);
    }
    .c_2022--body {
      @include fs_lh(18, 28, 18, 28);
      max-width: px_to_rem(700);
      align-self: center;
    }
    &__links_wrapper {
      display: flex;
      flex-direction: column;
      gap: px_to_rem(34);
      min-width: 0;
      flex-shrink: 0;
      @include mq($mq_md_to_lg) {
        min-width: px_to_rem(320px);
      }
    }
    &__ctas {
      gap: px_to_rem(16);
      display: flex;
      flex-direction: column;
      .c_button_2022__secondary {
        color: var(--header-standard-link-color);
        border-color: var(--header-standard-link-color);
        &:hover {
          border-color: var(--header-standard-color);
          background-color: var(--header-standard-color);
          color: var(--header-standard-bg-color);
        }
      }
    }
    &__links {
      display: flex;
      flex-flow: wrap;
      gap: px_to_rem(16);
      a,
      button {
        color: var(--header-standard-link-color);
        &:visited,
        &:active {
          color: var(--header-standard-link-color);
        }
        @include font('graphik_semibold');
        text-decoration: none;
        &:hover {
          text-decoration: underline;
        }
      }
    }
    &__media {
      margin-top: px_to_rem(80);
      margin-bottom: 0;
      img {
        object-fit: cover;
        object-position: center;
      }
      @media (max-width: $mq_sm_to_md + 'px') {
        margin-top: px_to_rem(42);
        padding-left: 0;
        padding-right: 0;
      }
    }
    &--condensed {
      margin-left: auto;
      margin-right: auto;
      padding-left: 5vw;
      padding-right: 5vw;
      max-width: 1150px;
      box-sizing: var(--wrapper-box-sizing, content-box);
      @media (max-width: $mq_sm_to_md + 'px') {
        padding-left: 0;
        padding-right: 0;
      }
    }
    &--expanded {
      margin-left: auto;
      margin-right: auto;
      max-width: 2560px;
      box-sizing: var(--wrapper-box-sizing, content-box);
    }
    &__media_wrapper {
      display: flex;
      overflow: hidden;
      justify-content: center;
      align-items: center;
    }
    &__overlay {
      background: rgba($color_black_raw, 0.5);

      @at-root #{&}__video {
        width: 90vw;
        max-width: 100%;
        overflow: hidden;
        @include mq($mq_sm_to_md) {
          width: 70vw;
        }
        @include mq($mq_md_to_lg) {
          width: px_to_rem(800);
        }
      }
    }
  }
}
.c_product_header_standard--left {
  .c_product_header_standard {
    justify-content: space-between;
    flex-direction: column;
    @include mq($mq_md_to_lg) {
      flex-direction: row;
    }
    .c_product_header__content_wrapper {
      text-align: left;
      .c_2022--body {
        a {
          color: var(--header-standard-text-link-color);
          text-decoration: underline;
          &:visited,
          &:active {
            color: var(--header-standard-text-link-color);
          }
          &:hover {
            text-decoration: none;
          }
        }
        b,
        strong {
          @include font('graphik_semibold');
        }
        i,
        em {
          @include font('graphik_regular_italic');
        }
        i strong {
          @include font('graphik_semibold_italic');
        }
      }
    }
    &__links_wrapper {
      align-items: stretch;
      .c_button_2022 {
        width: 100%;
      }
      @include mq($mq_sm_to_md) {
        align-items: flex-start;
      }
      @include mq($mq_md_to_lg) {
        align-items: flex-end;
      }
      .c_product_header_standard__links {
        justify-content: center;
        @include mq($mq_sm_to_md) {
          justify-content: flex-start;
        }
        @include mq($mq_md_to_lg) {
          justify-content: flex-end;
          flex-direction: column;
          align-items: flex-end;
        }
      }
    }
  }
}
.c_product_header_standard--center {
  .c_product_header_standard {
    gap: px_to_rem(42);
    flex-direction: column;
    text-align: center;
    .c_product_header__content_wrapper {
      text-align: center;
      display: flex;
      align-items: center;
      flex-direction: column;
    }
    &__links_wrapper {
      align-items: stretch;
      @include mq($mq_sm_to_md) {
        align-items: center;
      }
      .c_button_2022 {
        width: 100%;
      }
      .c_product_header_standard__links {
        justify-content: center;
      }
    }
  }
}
.c_product_header_standard--no-bottom-padding {
  @media (max-width: 2560px) {
    padding-bottom: 0;
  }
}
</style>
